import { validateCpf } from '@/app/utils/cpfFormater'
import { differenceInYears, isValid, parse } from 'date-fns'
import { z } from 'zod'

// Schema para informações pessoais
export const personalInformationSchema = z.object({
  name: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'Nome é obrigatório'),
  birthDate: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'Data de nascimento é obrigatória')
    .transform((value) => parse(value, 'dd/MM/yyyy', new Date()))
    .refine((value) => isValid(new Date(value)), 'Data Inválida')
    .refine((value) => {
      const age = differenceInYears(new Date(), value)
      return age >= 16 && age <= 100
    }, 'A data de nascimento deve estar entre 16 e 100 anos atrás. '),
  email: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'E-mail é obrigatório')
    .email('E-mail inválido'),
  phone: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'Telefone é obrigatório')
    .refine(
      (value) => value.replace(/\D/g, '').length === 11,
      'Telefone deve conter 11 dígitos',
    ),
  instagram: z.string().optional().or(z.literal('')),
  cpf: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'CPF é obrigatório')
    .refine((value) => {
      const cpf = value.replace(/\D/g, '')
      return cpf.length === 11
    }, 'CPF deve conter 11 dígitos')
    .refine((value) => validateCpf(value), 'CPF inválido'),
  rg: z.string().optional().or(z.literal('')),
})

// Schema para endereço
export const addressSchema = z.object({
  zipCode: z.string().min(1, 'CEP é obrigatório'),
  street: z.string().min(1, 'Rua é obrigatória'),
  number: z.string().optional(),
  complement: z.string().optional().or(z.literal('')),
  neighborhood: z.string().min(1, 'Bairro é obrigatório'),
  city: z.string().min(1, 'Cidade é obrigatória'),
  state: z.string().min(1, 'Estado é obrigatório'),
})

// Schema para senha
export const passwordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Senha deve conter no mínimo 8 caracteres')
      .refine(
        (value) => /[A-Z]/.test(value),
        'Senha deve conter pelo menos uma letra maiúscula',
      )
      .refine(
        (value) => /[a-z]/.test(value),
        'Senha deve conter pelo menos uma letra minúscula',
      )
      .refine(
        (value) => /\d/.test(value),
        'Senha deve conter pelo menos um número',
      )
      .refine(
        (value) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
        'Senha deve conter pelo menos um caractere especial',
      ),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  })

// Schema para dados bancários
export const bankSchema = z.object({
  bank: z.string().min(1, 'Banco é obrigatório'),
  agency: z.string().min(1, 'Agência é obrigatória'),
  account: z.string().min(1, 'Conta é obrigatória'),
  accountHolder: z
    .boolean()
    .refine((val) => val === true, 'Você deve declarar ser o titular da conta'),
  termsAccepted: z
    .boolean()
    .refine((val) => val === true, 'Você deve aceitar os termos e condições'),
  emailMarketing: z.boolean().optional(),
})

// Schema completo para validação final
export const registerSchema = z.object({
  personalInfo: personalInformationSchema,
  address: addressSchema,
  passwordInfo: passwordSchema,
  bankInfo: bankSchema,
})

export type RegisterFormData = z.infer<typeof registerSchema>
export type PersonalInformationData = z.infer<typeof personalInformationSchema>
export type AddressData = z.infer<typeof addressSchema>
export type PasswordData = z.infer<typeof passwordSchema>
export type BankData = z.infer<typeof bankSchema>

export type Step =
  | 'personal-information'
  | 'address'
  | 'password'
  | 'bank'
  | 'validate-identity'
  | 'analising'
  | 'RegisterApproved'
  | 'RegisterDenied'
